import { createFileRoute, redirect } from '@tanstack/react-router';
import { AGGREGATE_NAMES, LOAN_REQUEST_RESOLVERS, NOTE_CHANGE_TYPE, USER_RESOLVERS } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import {
    schemaLoanRequestResolver_Get,
    schemaLoanRequestResolver_GetAgreements,
    schemaLoanRequestResolver_GetApplicantNotes,
    schemaLoanRequestResolver_GetNotes,
    schemaLoanRequestResolver_GetOfferRequestConditionsLatest,
    schemaLoanRequestResolver_GetSystemNotes,
    schemaUserResolver_GetAdvisersByRole,
} from 'cpool-tables/backend-resolvers';

import { ROLES } from '~/constants';
import { authoriseUser } from '~/features/auth/utils/authoriseUser';
import { getSchufaQueryOptions } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/RefinancePlanner/hooks/useSchufa';
import { schemaGetConsorsNotesByLeadId } from '~/features/leadDetails/components/Overview/OfferManagement/Notes/schemas/getConsorsNotesByLeadId.schema';
import { aclPermissionsQueryOptions } from '~/features/leadDetails/routes/acceptedOffersQueryOptions';
import { leadDetailsSearchSchema } from '~/features/leadDetails/schemas/leadDetailsSearch.schema';
import { getCanonicalLeadDetailsHref } from '~/features/leadDetails/utils/normalizeLeadDetailsSearch';
import { getQueryOptions } from '~/queryUtils/getQueryOptions';

const leadDetailsQueryOptions = (leadId: string) =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.get,
        schema: schemaLoanRequestResolver_Get,
        searchParams: { id: leadId },
    });

const leadDetailsWithViewQueryOptions = (leadId: string) =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.get,
        schema: schemaLoanRequestResolver_Get,
        searchParams: { id: leadId, leadDetails: true },
    });

const getAdviserQueryOptions = (userIds: string[] = []) =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.USER,
        resolverName: USER_RESOLVERS.getAdvisersByRole,
        schema: schemaUserResolver_GetAdvisersByRole,
        searchParams: { ...(userIds.length ? { leadUserIds: userIds } : {}) },
        tanstack: {
            enabled: userIds.length > 0,
        },
    });

const leadNotesQueryOptions = (leadId: string) =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.getNotes,
        schema: schemaLoanRequestResolver_GetNotes,
        searchParams: { id: leadId },
    });
const getAgreementsQueryOptions = (leadId: string) =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.getAgreements,
        schema: schemaLoanRequestResolver_GetAgreements,
        searchParams: { id: leadId },
    });

const leadSystemNotesQueryOptions = (leadId: string) =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.getSystemNotes,
        schema: schemaLoanRequestResolver_GetSystemNotes,
        searchParams: { id: leadId },
    });

const leadApplicantNotesQueryOptions = (applicantIds: Array<string>, id: string) =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.getApplicantNotes,
        schema: schemaLoanRequestResolver_GetApplicantNotes,
        searchParams: { applicantIds, changeType: NOTE_CHANGE_TYPE.notes, id },
    });

const getOfferConditionsQueryOptions = ({ leadId }: { leadId: string }) =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.getOfferRequestConditionsLatest,
        schema: schemaLoanRequestResolver_GetOfferRequestConditionsLatest,
        searchParams: { id: leadId },
    });

const getConsorsNotesQueryOptions = ({ leadId }: { leadId: string }) =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.getConsorsNotesByLeadId,
        schema: schemaGetConsorsNotesByLeadId,
        searchParams: { id: leadId },
    });

export const Route = createFileRoute('/_app/leads_/details/$leadId')({
    beforeLoad: async ({ cause, context, location, params, search }) => {
        const canonicalHref = getCanonicalLeadDetailsHref(location.href, search, location.pathname);
        if (canonicalHref) throw redirect({ href: canonicalHref, replace: true });

        authoriseUser(Object.values(ROLES), location);
        const content = 'ROUTE_TITLE.LEAD_DETAILS';
        const opts =
            cause === 'preload'
                ? leadDetailsQueryOptions(params.leadId)
                : leadDetailsWithViewQueryOptions(params.leadId);
        const lead = (await context.queryClient.ensureQueryData(opts)) as SchemaLoanRequestResolver_Get;
        const userParams = {
            firstName: lead.antragsteller1.personendaten.vorname,
            lastName: lead.antragsteller1.personendaten.nachname,
        };
        return {
            meta: [
                {
                    content,
                    name: 'description',
                    params: userParams,
                },
                {
                    content,
                    name: 'title',
                    params: userParams,
                },
            ],
        };
    },
    loader: async ({ cause, context, params }) => {
        let lead: SchemaLoanRequestResolver_Get;
        if (cause === 'preload') {
            lead = (await context.queryClient.ensureQueryData(
                leadDetailsQueryOptions(params.leadId),
            )) as SchemaLoanRequestResolver_Get;
        } else {
            lead = (await context.queryClient.ensureQueryData(
                leadDetailsWithViewQueryOptions(params.leadId),
            )) as SchemaLoanRequestResolver_Get;
            context.queryClient.setQueryData(leadDetailsQueryOptions(params.leadId).queryKey, lead);
        }
        const applicantIds = [lead?.antragsteller1?.id, lead?.antragsteller2?.id].filter(Boolean) as string[];
        context.queryClient.ensureQueryData(getAgreementsQueryOptions(params.leadId));
        context.queryClient.ensureQueryData(getOfferConditionsQueryOptions({ leadId: params.leadId }));
        context.queryClient.ensureQueryData(getAdviserQueryOptions(lead?.userIds));
        context.queryClient.ensureQueryData(leadNotesQueryOptions(params.leadId));
        context.queryClient.ensureQueryData(leadSystemNotesQueryOptions(params.leadId));
        context.queryClient.ensureQueryData(leadApplicantNotesQueryOptions(applicantIds, params.leadId));
        context.queryClient.ensureQueryData(getConsorsNotesQueryOptions({ leadId: params.leadId }));
        const permissions = await context.queryClient.ensureQueryData(aclPermissionsQueryOptions(params.leadId));
        if (permissions?.resolvers[AGGREGATE_NAMES.LOAN_REQUEST][LOAN_REQUEST_RESOLVERS.getSchufa]) {
            context.queryClient.ensureQueryData(
                getSchufaQueryOptions({ applicantId: lead.antragsteller1.id, leadId: params.leadId }),
            );
        }
    },
    validateSearch: leadDetailsSearchSchema,
});
